/*
 * Final-render HTML trust boundaries shared by the framework clients.
 *
 * DOMPurify performs the parser-level cleanup. These named policies then make
 * each sink's intended capability explicit: translations get inline markup,
 * dashboard answers get document structure, and admin HTML fields get rich
 * media with tightly-scoped iframe embeds.
 */
const createDOMPurify = require('dompurify');

const TRUSTED_IFRAME_ORIGINS = Object.freeze([
  'https://www.youtube.com',
  'https://www.youtube-nocookie.com',
  'https://player.vimeo.com'
]);

const COMMON_ATTRS = ['class', 'title', 'aria-label', 'aria-hidden'];

// Every policy forbids these outright, whatever its tag list says. A policy
// may narrow the list for its own surface (see `email`), never widen it —
// 'script' is not negotiable on any sink.
const BASE_FORBID_TAGS = ['form', 'input', 'button', 'textarea', 'select', 'option', 'object', 'embed', 'script', 'style', 'svg', 'math'];
const BASE_FORBID_ATTR = ['style', 'srcdoc'];

const HTML_POLICIES = Object.freeze({
  translation: Object.freeze({
    tags: [
      'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3',
      'h4', 'h5', 'h6', 'i', 'kbd', 'li', 'mark', 'ol', 'p', 's', 'small',
      'span', 'strong', 'sub', 'sup', 'u', 'ul'
    ],
    attrs: COMMON_ATTRS.concat(['href', 'target', 'rel'])
  }),
  dashboard: Object.freeze({
    tags: [
      'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3',
      'h4', 'h5', 'h6', 'hr', 'i', 'kbd', 'li', 'mark', 'ol', 'p', 'pre',
      's', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td',
      'th', 'thead', 'tr', 'u', 'ul'
    ],
    attrs: COMMON_ATTRS.concat(['colspan', 'href', 'rel', 'rowspan', 'target'])
  }),
  admin: Object.freeze({
    tags: [
      'a', 'audio', 'b', 'blockquote', 'br', 'caption', 'code', 'col',
      'colgroup', 'div', 'em', 'figure', 'figcaption', 'h1', 'h2', 'h3',
      'h4', 'h5', 'h6', 'hr', 'i', 'iframe', 'img', 'kbd', 'li', 'mark',
      'ol', 'p', 'picture', 'pre', 's', 'small', 'source', 'span', 'strong',
      'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr',
      'u', 'ul', 'video'
    ],
    attrs: COMMON_ATTRS.concat([
      'alt', 'colspan', 'controls', 'height', 'href', 'loading', 'poster',
      'rel', 'rowspan', 'sandbox', 'src', 'srcset', 'target', 'type', 'width'
    ])
  }),
  // EMAIL BODIES ARE THE OPPOSITE CASE, and they get their own policy rather
  // than a loosening of anyone else's (card 6a810a755ddf52032438c3e3).
  //
  // The other policies forbid `style` because a stored stylesheet aimed at one
  // of OUR rendering surfaces is a CSS-exfiltration / UI-redressing tool. An
  // email body is not one of our surfaces: it is a document handed to Gmail
  // and Outlook, which strip external and <style>-block CSS, so real email
  // carries its styling as inline `style=` attributes and its layout as
  // tables. Running Message.html through a fragment policy would strip the
  // styling that makes the mail render at all, and drop <html>/<head>/<body>
  // on the floor — silently, between save and reload.
  //
  // So this policy keeps what email needs (style attributes, style blocks,
  // table layout, the document shell) and spends its strictness where the
  // actual risk on this surface is: SCRIPT. The admin renders a body in a
  // srcDoc iframe, which is same-origin, so a <script> in a stored body would
  // run with the admin's privileges. Scripts, event handlers, frames and form
  // controls are removed; the mail client would drop them anyway.
  email: Object.freeze({
    tags: [
      'a', 'b', 'blockquote', 'body', 'br', 'caption', 'center', 'code', 'col',
      'colgroup', 'div', 'em', 'figure', 'figcaption', 'font', 'h1', 'h2',
      'h3', 'h4', 'h5', 'h6', 'head', 'hr', 'html', 'i', 'img', 'li', 'mark',
      'meta', 'ol', 'p', 'picture', 'pre', 's', 'small', 'source', 'span',
      'strike', 'strong', 'style', 'sub', 'sup', 'table', 'tbody', 'td',
      'tfoot', 'th', 'thead', 'title', 'tr', 'u', 'ul'
    ],
    attrs: COMMON_ATTRS.concat([
      'align', 'alt', 'bgcolor', 'border', 'cellpadding', 'cellspacing',
      'charset', 'color', 'colspan', 'content', 'dir', 'face', 'height',
      'href', 'lang', 'name', 'rel', 'rowspan', 'size', 'src', 'srcset',
      'style', 'target', 'type', 'valign', 'width'
    ]),
    // The two deliberate departures from the shared default, each named so a
    // reader can see the cost being accepted.
    allowStyle: true,
    wholeDocument: true
  })
});

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function browserPurifier() {
  if (typeof createDOMPurify.sanitize === 'function') return createDOMPurify;
  if (typeof window !== 'undefined' && window.document) return createDOMPurify(window);
  return null;
}

function safeUrl(raw, kind) {
  const value = String(raw || '').trim();
  if (!value) return false;
  if (kind === 'href' && /^(?:#|\/(?!\/)|\.{1,2}\/)/.test(value)) return true;
  if (kind === 'src' && /^(?:\/(?!\/)|\.{1,2}\/)/.test(value)) return true;
  if (kind === 'img' && /^data:image\/(?:gif|jpeg|png|webp);base64,/i.test(value)) return true;

  try {
    const parsed = new URL(value, 'https://sanitizer.invalid');
    if (kind === 'href') return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch (_) {
    return false;
  }
}

function userMessageText(message) {
  const value = String(message && message.text != null ? message.text : '');
  if (message && message.userTextLiteral === true) return value;

  // Dashboard rows written before the literal-text marker wrapped untrusted
  // bytes in framework-owned markup. Strip only that exact historical shell;
  // the returned bytes are still rendered as text by React or escaped before
  // an export HTML boundary.
  const legacy = value.match(/^<pre><code>([\s\S]*)<\/code><\/pre>$/i);
  return legacy ? legacy[1] : value;
}

function dashboardMessageHtml(message) {
  if (message && message.sender === 'user') {
    return `<pre><code>${escapeHtml(userMessageText(message))}</code></pre>`;
  }
  return sanitizeDashboardHtml(message && message.text);
}

function safeDashboardHref(raw) {
  const value = String(raw == null ? '' : raw).trim();
  return safeUrl(value, 'href') ? value : null;
}

function safeFilenameSegment(raw, fallback = 'export') {
  const value = String(raw == null ? '' : raw)
    .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return value || fallback;
}

function trustedIframe(raw) {
  try {
    const url = new URL(String(raw || ''));
    if (!TRUSTED_IFRAME_ORIGINS.includes(url.origin)) return false;
    if (url.origin === 'https://player.vimeo.com') return /^\/video\/[^/]+/.test(url.pathname);
    return /^\/embed\/[^/]+/.test(url.pathname);
  } catch (_) {
    return false;
  }
}

function constrainUrls(html, policyName) {
  if (typeof document === 'undefined' || !document.createElement) return escapeHtml(html);
  const policy = HTML_POLICIES[policyName];

  // A whole-document policy cannot round-trip through <template>: the parser
  // drops <html>/<head>/<body> inside one, which would throw away the very
  // structure the policy exists to keep. Parse it as a document instead and
  // run the same URL constraints over it.
  if (policy && policy.wholeDocument) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    constrainUrlsIn(parsed, policyName);
    return parsed.documentElement ? parsed.documentElement.outerHTML : html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;
  constrainUrlsIn(template.content, policyName);
  return template.innerHTML;
}

function constrainUrlsIn(root, policyName) {
  for (const node of root.querySelectorAll('[href]')) {
    if (!safeUrl(node.getAttribute('href'), 'href')) node.removeAttribute('href');
    if (node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer');
    else node.removeAttribute('target');
  }
  for (const node of root.querySelectorAll('img[src]')) {
    if (!safeUrl(node.getAttribute('src'), 'img')) node.removeAttribute('src');
  }
  for (const node of root.querySelectorAll('audio[src], video[src], source[src]')) {
    if (!safeUrl(node.getAttribute('src'), 'src')) node.removeAttribute('src');
  }
  for (const node of root.querySelectorAll('iframe')) {
    if (policyName !== 'admin' || !trustedIframe(node.getAttribute('src'))) {
      node.remove();
      continue;
    }
    node.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
    node.setAttribute('loading', 'lazy');
  }
}

function sanitizeWithPolicy(value, policyName) {
  const policy = HTML_POLICIES[policyName];
  if (!policy) throw new Error(`Unknown HTML sanitization policy: ${policyName}`);
  const purifier = browserPurifier();
  if (!purifier) return escapeHtml(value);

  // A policy may only ever SUBTRACT from what it allows, so the forbid lists
  // are derived from the shared base rather than written out per policy: a
  // policy that declares `style` in its own tag list keeps <style>, and one
  // that sets allowStyle keeps the attribute. Everything else — script above
  // all — stays forbidden for every sink no matter what its tag list says.
  const forbidTags = BASE_FORBID_TAGS.filter((t) => t === 'script' || !policy.tags.includes(t));
  const forbidAttr = policy.allowStyle ? BASE_FORBID_ATTR.filter((a) => a !== 'style') : BASE_FORBID_ATTR;

  const clean = purifier.sanitize(String(value == null ? '' : value), {
    ALLOWED_TAGS: policy.tags,
    ALLOWED_ATTR: policy.attrs,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: true,
    FORBID_TAGS: forbidTags,
    FORBID_ATTR: forbidAttr,
    KEEP_CONTENT: true,
    WHOLE_DOCUMENT: !!policy.wholeDocument
  });
  return constrainUrls(clean, policyName);
}

const sanitizeTranslationHtml = (value) => sanitizeWithPolicy(value, 'translation');
const sanitizeDashboardHtml = (value) => sanitizeWithPolicy(value, 'dashboard');
const sanitizeAdminHtml = (value) => sanitizeWithPolicy(value, 'admin');

module.exports = {
  HTML_POLICIES,
  TRUSTED_IFRAME_ORIGINS,
  dashboardMessageHtml,
  escapeHtml,
  safeDashboardHref,
  safeFilenameSegment,
  sanitizeAdminHtml,
  sanitizeDashboardHtml,
  sanitizeTranslationHtml,
  sanitizeWithPolicy,
  trustedIframe,
  userMessageText
};
